import { useState, useEffect } from "react";
import { bereavementsApi } from "@/lib/api";
import { CalendarDots, Heartbeat } from "@phosphor-icons/react";
import { format } from "date-fns";
import { bereavementSubtitle } from "@/lib/bereavement";

export default function BereavementFeed({ onSelect }) {
  const [bereavements, setBereavements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bereavementsApi.list().then(({ data }) => { setBereavements(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-4" data-testid="bereavement-feed">
      <h2 className="font-heading text-2xl font-semibold tracking-tight">Bereavements</h2>
      {bereavements.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Heartbeat className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No bereavements posted yet</p>
        </div>
      ) : (
        bereavements.map((b) => (
          <button
            key={b.id}
            onClick={() => onSelect(b.id)}
            data-testid={`bereavement-card-${b.id}`}
            className="w-full text-left bg-card border border-border rounded-xl p-5 shadow-sm hover:border-primary/30 transition-all active:scale-[0.99]"
          >
            <div className="flex gap-4 items-start">
              {b.imageUrl ? (
                <img
                  src={b.imageUrl}
                  alt={b.deceasedName}
                  className="w-16 h-16 rounded-xl object-cover bg-muted border border-border shrink-0"
                  loading="lazy"
                  data-testid={`bereavement-thumb-${b.id}`}
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-muted border border-border shrink-0 flex items-center justify-center" data-testid={`bereavement-thumb-placeholder-${b.id}`}>
                  <Heartbeat className="w-7 h-7 text-muted-foreground opacity-40" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{b.relationship}</span>
                  {b.status === "active" ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Active
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Closed</span>
                  )}
                </div>
                <p className="font-heading font-semibold text-foreground text-lg leading-tight truncate">{b.deceasedName}</p>
                <p className="text-sm text-muted-foreground mt-0.5 truncate">{bereavementSubtitle(b)}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1">
                    <CalendarDots className="w-3.5 h-3.5" />
                    {b.dateOfDeath ? format(new Date(b.dateOfDeath), "dd MMM yyyy") : "—"}
                  </span>
                  <span className="font-semibold text-emerald-700">+ KES {(b.totalContributed || 0).toLocaleString()}</span>
                  {b.totalDisbursed > 0 && <span className="font-semibold text-orange-600">− KES {(b.totalDisbursed || 0).toLocaleString()}</span>}
                  <span className={`font-semibold ${(b.balance || 0) < 0 ? "text-red-600" : "text-primary"}`}>= KES {(b.balance || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </button>
        ))
      )}
    </div>
  );
}
